import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {PearlHarborGameAssets} from "../PearlHarborGameAssets";
import {PearlHarbor} from "../PearlHarbor";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {PearlHarborHelper} from "../utils/PearlHarborHelper";
import {PearlHarborSoundManager} from "../sound/PearlHarborSoundManager";
import Spine = PIXI.spine.Spine;

export class PearlHarborBigWin extends PIXI.Container implements StageLayer {
    private _countUp: BasicBitmapCountUp;
    private _overlay: PIXI.Graphics;
    private _spines: PIXI.spine.Spine[];
    private _zoomTime:number = 0.26;
    constructor() {
        super();
        this.name = "BIG_WIN";
        this._spines = [];
        this.init();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        const isLandscape:boolean = NolimitApplication.isLandscape,
            isIpad:boolean = PearlHarborHelper.isIpad;
        const zoom:number = isLandscape && !isIpad?1:0.7,
            x:number = isLandscape && !isIpad?0:100,
            y:number = isLandscape && !isIpad?0:100;
        TweenLite.to(this.scale, this._zoomTime, {
            x : zoom, y : zoom
        });
        TweenLite.to(this.position, this._zoomTime, {
            x : x, y : y
        });
        this._overlay.clear();
        this._overlay.beginFill(0x000000, 0.5);
        this._overlay.drawRect(bounds.left, bounds.top, bounds.width, bounds.height);
    }

    public precalculateScale(endValue: number): void {
        this._countUp.stopAt(0);
        this._countUp.preCalculateScale(0, endValue);
    }

    public show(duration: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        this._overlay.alpha = 0;
        this._overlay.visible = true;
        tl.add(TweenLite.to(this._overlay, 0.2, {
            alpha: 1
        }));
        this.alpha = 0;
        this.visible = true;
        tl.add(gsap.to(this, {duration: duration, alpha: 1}));
        return tl;
    }

    public hide(duration: number = 0.0001): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.to(this, {duration: duration, alpha: 0});
        tl.add(() => {
            this._overlay.visible = false;
            this.visible = false;
            this._countUp.visible = false;
            this._spines.forEach((spine: PIXI.spine.Spine) => {
                spine.visible = false;
                PearlHarborHelper.resetSpineItem(spine);
                spine.state.setEmptyAnimation(1, 0);
                spine.state.setEmptyAnimation(2, 0);
                spine.state.setEmptyAnimation(3, 0);
            });
        });
        return tl;
    }

    private getSpine(level: number): Spine {
        let spine: Spine;
        if (this._spines[level - 1]) {
            spine = this._spines[level - 1];
            spine.updateTransform();
            spine.state.clearTracks();
            spine.skeleton.setSlotsToSetupPose();
            spine.skeleton.setToSetupPose();
        } else {
            spine = new PIXI.spine.Spine(PearlHarbor.assetLoader.getSpineAsset("big_win_lvl_" + level));
            spine.position.set(360, 360);
            spine.name = "BIGWIN_SPINE_LEVEL_" + level;
            this._spines.push(spine);
        }
        spine.alpha = 0;
        spine.visible = true;
        return spine;
    }

    public getLevelAnimation(countFrom: number, countTo: number, duration: number, level: number): gsap.core.Timeline {
        Logger.logDev("getLevelAnimation called details :: from : " + countFrom + " to : " + countTo + " duration : " + duration + " level : " + level);
        const tl: TimelineLite = new TimelineLite(),
            spine: PIXI.spine.Spine = this.getSpine(level),
            maxLevel: number = PearlHarborHelper.getBigWinLevel();

        tl.add(() => {
            spine.alpha = 1;
            this.addChild(spine);
            this.addChild(this._countUp);
            if (level === 1) {
                PearlHarborSoundManager.fadeMusic(0, 1200, true);
            } else {
                this._spines[level - 2].visible = false;
                PearlHarborHelper.resetSpineItem(this._spines[level - 2]);
            }
        });
        if (level === 3) {
            tl.add(() => {
                spine.state.setAnimation(0, "big_win_track_01", true);
                spine.state.setAnimation(1, "big_win_track_02", true);
                spine.state.setAnimation(2, "big_win_track_03", true);
                spine.state.setAnimation(3, "big_win_track_04", true);
            });

        } else if (level === 2) {
            tl.add(() => {
                spine.state.setAnimation(0, "ammobelt", true);
                spine.state.setAnimation(1, "emptyShels", true);
                spine.state.setAnimation(2, "big_win", true);
            });

        } else {
            tl.add(() => {
                spine.state.setAnimation(0, "big_win", true);
            });
        }
        tl.add(() => {
            PearlHarborSoundManager.playBigWinSound(level);
        }, 0);
        if(level == 1) {
            duration -= 1.103;
        }
        const scaleTl: TimelineLite = new TimelineLite();
        scaleTl.add(this.getCountUpScaleAnimation(0.1, "power2.in", 0.8));
        scaleTl.add(this.getCountUpScaleAnimation(0.2, "power2.out", 1.0));
        scaleTl.add(this.getCountUpScaleAnimation(duration - 0.3, "power2.in", 1.2));
        if (level == 1) {
            tl.add(scaleTl, 1.103);
            tl.add(this._countUp.getCountAnimation(countFrom, countTo, duration - 0.1), 1.203);
            tl.add(() => {
                this._countUp.y = 580;
                this._countUp.visible = true;
            }, 1.203);
        } else {
            tl.add(scaleTl, 0.1);
            tl.add(this._countUp.getCountAnimation(countFrom, countTo, duration - 0.1), 0.1);
        }
        tl.add(() => {
            if (level === maxLevel) {
                PearlHarborSoundManager.playBigWinEndSound();
                PearlHarborSoundManager.fadeMusic(1, 1000, false);
            }
        });

        return tl;
    }

    public onAbort(level: number): void {
        const maxLevel: number = PearlHarborHelper.getBigWinLevel();
        if (level !== maxLevel) {
            PearlHarborSoundManager.playBigWinSkipSound();
        }
    }

    private getCountUpScaleAnimation(duration: number, ease: string, to: number, from?: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        if (from != undefined) {
            tl.add(() => {
                this._countUp.scale.set(from, from)
            });
        }
        tl.add(gsap.to(this._countUp.scale, {duration: duration, x: to, y: to, ease: ease}));
        return tl;
    }

    private init(): void {
        this._overlay = new PIXI.Graphics();
        this._overlay.visible = false;
        this._countUp = new BasicBitmapCountUp({
            fontName: PearlHarborGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 290,
            tint: 0xffffff
        }, 520, new PIXI.Point(0.5, 0.5));
        this._countUp.position.set(360, 580);
        this._countUp.scale.set(0.6, 0.6);
        this._countUp.stopAt(0);
        this.addChildAt(this._overlay, 0);

        this.addChild(this._countUp);
        this.hide();
        this.onResize();
    }
}